// app/src/features/layout/ForecastManagerOverlay.jsx
// `ctx` is the frame's context (AppFrame's); the store is reached only through the `dispatchRun` /
// `getRunState` handles it carries (C-23), never imported here.
import ForecastsHeading from "../../components/ForecastsHeading.jsx";
import { WatchlistLogContent } from "../../components/ForecastLog.jsx";
import { DrawerHelp, HelpReopenButton, OverlayPanel } from "../../components/Panels.jsx";
import { clearForecastAlert, deleteSelection } from "../../commands/deleteForecast.js";
import { noteRowOpen } from "../../hooks/useForecastOpenIntent.js";
import { toggleCompare } from "../../lib/comparison";
import { captureChatTarget } from "../../lib/forecast/chatTarget.js";
import { activeSet, comparisonCleared, comparisonSet } from "../../lib/forecast/reducer.js";
import { shouldShowRunningRowAsGenerating } from "../../lib/forecast/resume.js";
import { sharedRerunHandleFor } from "../../lib/forecast/storageKeys.js";
import { selectLegacyForecastById } from "../../lib/forecast/selectors.js";
import { recordUiEvent } from "../../lib/logRingBuffer";
import { RAIL_SECTIONS } from "../../lib/navRail.js";
import { isConcluded, isResolvedWatchlisted, shouldSeedWatchEmail } from "../../lib/tracking.js";
import { LOG_HELP_TRACKING, markDrawerHelpSeen } from "../../onboarding/drawerHelp";

/** The Forecast Manager panel: the board, its search and help, and the row handlers. */
export default function ForecastManagerOverlay({ ctx }) {
  const {
    active,
    autoZoom,
    bufferForContract,
    closeHintShownRef,
    compareLaneOf,
    compareMode,
    comparison,
    contractOpen,
    debugResolutionOverride,
    debugUI,
    dispatchRun,
    drawerHelpState,
    drawerZ,
    effectiveToday,
    forecastAbortRef,
    forecastOrder,
    forecastSearch,
    forecastStacking,
    forecastTracking,
    forecasts,
    forecastsById,
    getRunState,
    getTrackingPrefs,
    handleReorderForecast,
    handleSelectRef,
    isWatchlisted,
    lastLayoutRef,
    loadConversationThread,
    loadForecastSeries,
    logOpen,
    mkDeleteDeps,
    moveForecastInOrder,
    notifyEmailEnabled,
    onLoadMoreShared,
    openShareDialog,
    orderedLogForecasts,
    patchTrackingPrefs,
    pinnedRef,
    pushSayerMsg,
    railResizeHandle,
    railSwitched,
    selectForecast,
    setAnimPhase,
    setBranchAnim,
    setCollapsedIds,
    setDeleteConfirm,
    setDrawerHelpState,
    setForecastingPhase,
    setLoading,
    setPromotedIds,
    setTrackingSettingsId,
    setWatchlistLimitPopup,
    shareDialogAvailable,
    sharedError,
    sharedForecasts,
    sharedHasMore,
    sharedLoading,
    slideCache,
    theme,
    toggleWatchlist,
    tourActive,
    tsDrawerOpen,
    WATCHLIST_LIMIT,
  } = ctx;
  return (
    <OverlayPanel
      side="left"
      open={logOpen}
      zOverride={drawerZ.log}
      // The rail panel's width is live during a resize drag, so it is the CSS variable.
      width="var(--rail-panel-w)"
      instant={railSwitched}
      className="rail-panel"
      data-testid="forecast-log"
      surface="forecast-manager"
    >
      <ForecastsHeading label={RAIL_SECTIONS[0].heading} search={forecastSearch} />
      {logOpen && (
        <div
          data-chat-resize=""
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize Forecast Manager"
          title="Drag to resize · double-click to reset"
          {...railResizeHandle}
          className="rail-resize-handle"
        />
      )}
      {logOpen && !drawerHelpState.log && (
        <DrawerHelp
          k="log"
          surface="forecast-manager"
          bodyOverride={forecastTracking ? LOG_HELP_TRACKING : undefined}
          onClose={() => {
            markDrawerHelpSeen("log");
            setDrawerHelpState((s) => ({ ...s, log: true }));
          }}
        />
      )}
      {logOpen && drawerHelpState.log && (
        <HelpReopenButton
          corner="br"
          surface="forecast-manager"
          onClick={() => setDrawerHelpState((s) => ({ ...s, log: false }))}
        />
      )}
      {(() => {
        const handleSelect = (f, e) => {
          if (e?.shiftKey && compareMode) {
            // Lane 0 is anchored to the currently active forecast, so the primary spine and
            // the comparison selection stay consistent (the spine renders from active,
            // compare lanes from compareRoots.slice(1)).
            const activeId = active?.forecastId || active?.id || null;
            const clickedId = f.forecastId || f.id;
            // Reading `comparison` directly is safe: this is a synchronous user-event
            // handler, not an async callback, so there is no stale-closure risk.
            {
              // Full-store fallback so a compare root OLDER than the capped
              // display list (now possible via ?compare= hydration) isn't lost
              // when a shift+click edits the selection — it would otherwise miss
              // the capped `forecasts` and drop from nextRunIds below.
              const store = getRunState();
              const canon = (rid) => {
                const fc =
                  forecasts.find((x) => x.forecastId === rid || x.id === rid) ||
                  (active && (active.forecastId === rid || active.id === rid) ? active : null) ||
                  selectLegacyForecastById(store, rid);
                return fc ? fc.forecastId || fc.id : rid;
              };
              let ids = (comparison.rootIds || [])
                .map(canon)
                .filter((v, i, a) => a.indexOf(v) === i);
              if (ids.length === 0 && activeId) ids = [activeId];
              const nextSel = toggleCompare({ rootIds: ids }, clickedId);
              if (nextSel.rootIds.length === 0) {
                dispatchRun(comparisonCleared());
              } else {
                const nextRunIds = nextSel.rootIds
                  .map((rid) => {
                    const fc =
                      forecasts.find((x) => x.id === rid || x.forecastId === rid) ||
                      (active && (active.id === rid || active.forecastId === rid)
                        ? active
                        : null) ||
                      selectLegacyForecastById(store, rid);
                    return fc ? fc.id : null;
                  })
                  .filter(Boolean);
                dispatchRun(comparisonSet(nextRunIds));
                // If the user deselected the ACTIVE root, move active to the surviving
                // lead. Otherwise the deselected row keeps its isActive highlight — and
                // for the primary that highlight is the SAME purple as lane 0, so it looks
                // "still selected" — while active diverges from compareRoots[0]. Following
                // the lead clears the deselected row and preserves the active===lead
                // invariant (the compareOn-gated pin-to-lead effect can't, since dropping
                // to one root turns compareOn off).
                const activeRunId = active?.id || null;
                if (activeRunId && !nextRunIds.includes(activeRunId) && nextRunIds[0]) {
                  dispatchRun(activeSet(nextRunIds[0]));
                }
              }
            }
            return;
          }
          // Excluded in compare mode, where a plain click collapses the comparison onto that
          // root. Clicking the row that's ALREADY highlighted opens its tracking settings
          // instead of a no-op re-select; the settings flyover exists only in the tracking
          // view.
          if (forecastTracking && !compareMode && active && f && active.id === f.id) {
            handleOpenSettings(f);
            return;
          }
          noteRowOpen();
          // The id passed is always the runId (client id), not the forecastId.
          dispatchRun(comparisonSet([f.id]));
          recordUiEvent({
            event: "ui.forecast.switch",
            from_id: active?.id || null,
            from_forecast_id: active?.forecastId || null,
            from_status: active?.status || null,
            to_id: f?.id || null,
            to_forecast_id: f?.forecastId || null,
            to_trace_id: f?.traceId || null,
            to_status: f?.status || null,
          });
          // Clear all per-forecast transient state so the loaded forecast renders cleanly.
          // Without this, slideCache + animation state from the previous forecast can suppress
          // the spine and trigger events on the newly-loaded one.
          // eslint-disable-next-line react-hooks/immutability -- slideCache is AppInner's useRef (the per-event label anchors), handed in without the Ref suffix; a click handler, not render
          slideCache.current = {};
          lastLayoutRef.current = new Map();
          setPromotedIds(new Set());
          setCollapsedIds(new Set());
          setAnimPhase(null);
          setBranchAnim(null);
          setForecastingPhase(null);
          // The time-series selection is per-forecast transient state, so it is loaded HERE,
          // above the running-row branch that returns early, making every selection path
          // authoritative. A forecast with no saved signals CLEARS the panel rather than
          // inheriting the previous one's.
          loadForecastSeries(f);
          if (forecastTracking && f.alert) {
            clearForecastAlert(mkDeleteDeps(), { runId: f.id });
          }
          // Navigating away must not kill an in-flight forecast. `loading` is dropped only
          // when no doForecast is in flight in this tab, so submit()'s `loading` guard can't
          // be bypassed mid-run.
          if (!forecastAbortRef.current) setLoading(false);
          // A shared running row is withheld from this: its live updates arrive over the
          // generic realtime hints channel and this tab never attaches a stream to it (see
          // shouldReattachOnSelect), so the awaiting animation would stay stuck with nothing
          // to clear it. It falls through to the plain "just show this row" path below —
          // unless this tab started the update it is running, which it does attach to.
          if (shouldShowRunningRowAsGenerating(f, sharedRerunHandleFor(f?.id))) {
            if (f.contract?.resolution_date)
              autoZoom(
                f.contract.resolution_date,
                true,
                f.createdAt ?? null,
                bufferForContract(f.contract, f),
                !isConcluded(f),
              );
            setForecastingPhase("awaiting");
            // If the trace isn't owned in THIS tab, selecting is a genuine RECONNECT, so
            // reattach seeds "Resuming forecast…" (not "Generating Forecast", which belongs
            // to a fresh submit). If it IS owned here (submitted here, navigated away,
            // clicked back), the original doForecast/reattach still owns the stream and
            // reattach no-ops on the failed claim. seedChat is false during the tour so a
            // peek doesn't overwrite it.
            selectForecast(f, { reattach: true, seedChat: !tourActive });
            // In-flight branches of this forecast remain in the run store across
            // navigation, so their run-projected chat lines reappear and any live
            // stream (owned by execByRunIdRef) keeps driving them; a reload recovers
            // them from their persisted server rows via the resume sweep.
            return;
          }
          dispatchRun(activeSet(f.id));
          // During the tour, only load the forecast onto the timeline; preserve the chat (and
          // the tour) rather than replacing the conversation.
          if (!tourActive && f.forecastId) {
            loadConversationThread(f.forecastId, f.id || null);
          }
          // Framed EXACTLY as the CENTER button frames it: same args, including
          // `f.createdAt`, matching centerToday in hooks/useTimelineZoom.js. Selecting a
          // forecast and centering it must land on the same zoom, and passing createdAt makes
          // the frame show the whole lifespan, so GENERATED and every reforecast vintage
          // older than today stay on screen.
          if (f.contract?.resolution_date)
            autoZoom(
              f.contract.resolution_date,
              true,
              f.createdAt ?? null,
              bufferForContract(f.contract, f),
              // keepTodayInFrame: anchoring the left edge at createdAt makes the frame
              // span creation→resolution, which puts today OUTSIDE it as soon as the
              // resolution date passes. Everything hung off the today line renders only
              // while `todayX` is on screen, so the ↻ REFORECAST pill vanished on exactly
              // the forecasts it exists for: unresolved ones past their date. A concluded
              // row keeps the lifespan frame (centerToday's resolved branch does too).
              !isConcluded(f),
            );
        };
        // Render-time ref assignment forwards the latest handleSelect to the ?forecast=
        // deep-link effect, like the other forward refs.
        handleSelectRef.current = handleSelect;
        const handlePin = (id, at) => {
          // Read the synchronous mirror (not render-closure `pinned`) so
          // the cap check sees pins from rapid prior clicks not yet
          // committed. toggleWatchlist updates the mirror for us.
          const cur = pinnedRef.current;
          // BOTH LISTS — `forecasts` is the owner leg alone, and the rules
          // below distinguish an owned row from a shared one, so a lookup
          // that cannot see shared rows decides them wrongly.
          const fc = forecastsById.get(id);
          // A resolved contract kept on the watchlist (isResolvedWatchlisted)
          // is NOT in the server `pinned` map, so the Eye must REMOVE it
          // (the unwatch clears the schedule → drops it off) rather than re-pin a terminal
          // contract — re-pinning would PATCH a fresh schedule and seed email
          // alerts on an already-resolved contract, and take two clicks.
          const resolvedKept = isResolvedWatchlisted(fc);
          const wasPinned = !!cur[id] || resolvedKept;
          if (wasPinned) {
            // The server stops its schedule once nobody is left watching (toggleWatchlist).
            toggleWatchlist(id, false, at);
            // Email alerts are the viewer's own on every row (a recipient's live in their own
            // notify record), so they stop with the watch, shared or not.
            if (getTrackingPrefs(id).email) patchTrackingPrefs(id, { email: false });
            return;
          }
          const pinnedCount = Object.values(cur).filter(Boolean).length;
          if (pinnedCount >= WATCHLIST_LIMIT) {
            setWatchlistLimitPopup(true);
            return;
          }
          // The server starts an unscheduled forecast Weekly (toggleWatchlist). Email alerts
          // turn on for any row, with the threshold persisted beside them: the notifier gates
          // on the server's notify, not the client default.
          toggleWatchlist(id, true, at);
          if (shouldSeedWatchEmail(fc, notifyEmailEnabled)) {
            patchTrackingPrefs(id, { email: true, thrIdx: getTrackingPrefs(id).thrIdx });
          }
          if (
            tourActive &&
            !closeHintShownRef.current &&
            (logOpen || contractOpen || tsDrawerOpen)
          ) {
            closeHintShownRef.current = true;
            pushSayerMsg("**Click the drawer's title bar** to close it.", {
              kind: "tour",
              target: captureChatTarget(getRunState()),
            });
          }
        };
        // Watchlist-only: clicking the meta line / sliders icon opens the Tracking Settings
        // flyover AND clears the row's alert, without swapping the active forecast. It must
        // not call handleSelect, which does much more than select (clears slideCache, resets
        // anim state, calls autoZoom, reloads time-series) and would shift the timeline
        // viewport every time a user opened settings. The alert is cleared inline instead.
        const handleOpenSettings = (f) => {
          if (f && f.alert) {
            clearForecastAlert(mkDeleteDeps(), { runId: f.id });
          }
          setTrackingSettingsId(f.id);
        };
        return (
          <WatchlistLogContent
            theme={theme}
            forecasts={orderedLogForecasts}
            isWatchlisted={isWatchlisted}
            active={active}
            onSelect={handleSelect}
            onTogglePin={handlePin}
            onReorder={handleReorderForecast}
            onOpenSettings={handleOpenSettings}
            // onOpenShare is passed ONLY when the dialog is actually reachable, so the badge
            // stays a plain label rather than a control that does nothing where the gate is
            // shut. The badge hands over its OWN row.
            onOpenShare={shareDialogAvailable ? (f) => openShareDialog(f) : undefined}
            getTrackingPrefs={getTrackingPrefs}
            compareLaneOf={compareLaneOf}
            compareMode={compareMode}
            debugUI={debugUI}
            debugResolutionOverride={debugResolutionOverride}
            nowMs={effectiveToday.getTime()}
            sharedForecasts={sharedForecasts}
            forecastOrder={forecastOrder}
            sharedLoading={sharedLoading}
            sharedHasMore={sharedHasMore}
            sharedError={sharedError}
            onLoadMoreShared={onLoadMoreShared}
            stacking={forecastStacking}
            onMoveInOrder={moveForecastInOrder}
            watchlistLimit={WATCHLIST_LIMIT}
            onWatchlistFull={() => setWatchlistLimitPopup(true)}
            onDelete={(f) => setDeleteConfirm(deleteSelection(f))}
            filterQuery={forecastSearch.query}
          />
        );
      })()}
    </OverlayPanel>
  );
}
